import { motion } from 'framer-motion';
import { dpsTheme, getScoreLevelConfig } from '../../styles/dpsTheme';
import { SavedDiagnostic } from '../../services/diagnosticService';
import { useDeviceSize } from '../../hooks/useMediaQuery';

interface DiagnosticCardProps {
  diagnostic: SavedDiagnostic;
  onViewFreeReport: () => void;
  onViewPremiumReport: () => void;
  onVerifyPayment: () => void;
  isVerifying: boolean;
  /** Masque les boutons de paiement (Débloquer/Vérifier) pour les rôles staff */
  hidePaymentButtons?: boolean;
  /** Indique qu'il y a une notification non lue sur ce diagnostic */
  hasUnreadNotification?: boolean;
}

export const DiagnosticCard: React.FC<DiagnosticCardProps> = ({
  diagnostic,
  onViewFreeReport,
  onViewPremiumReport,
  onVerifyPayment,
  isVerifying,
  hidePaymentButtons = false,
  hasUnreadNotification = false,
}) => {
  const { isMobile } = useDeviceSize();
  const isPremium = diagnostic.report_paid;
  const currentScore = (diagnostic as any).current_score ?? diagnostic.score;
  const scoreConfig = getScoreLevelConfig(currentScore);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: dpsTheme.colors.background.card,
        border: `1px solid ${
          hasUnreadNotification
            ? 'rgba(251, 146, 60, 0.4)'
            : isPremium
            ? dpsTheme.colors.status.completed.border
            : dpsTheme.colors.border.primary
        }`,
        borderRadius: dpsTheme.borderRadius.lg,
        padding: isMobile ? dpsTheme.spacing.md : dpsTheme.spacing.xl,
        transition: 'all 0.2s',
        cursor: 'pointer',
        position: 'relative',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = dpsTheme.shadows.hover;
        e.currentTarget.style.borderColor = isPremium ? dpsTheme.colors.status.completed.primary : dpsTheme.colors.accent.blue.primary;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = isPremium ? dpsTheme.colors.status.completed.border : dpsTheme.colors.border.primary;
      }}
    >
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'flex-start', gap: isMobile ? '16px' : '24px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
            {/* Badge notification non lue */}
            {hasUnreadNotification && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '3px 8px',
                  borderRadius: '10px',
                  fontSize: '11px',
                  fontWeight: 700,
                  background: 'rgba(251, 146, 60, 0.15)',
                  border: '1px solid rgba(251, 146, 60, 0.35)',
                  color: '#fb923c',
                  flexShrink: 0,
                  letterSpacing: '0.2px',
                }}
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                Mise à jour
              </span>
            )}

            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: dpsTheme.typography.fontSize.xs,
                fontWeight: dpsTheme.typography.fontWeight.bold,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                background: isPremium ? dpsTheme.colors.status.completed.bg : dpsTheme.colors.status.notPlanned.bg,
                border: `1px solid ${isPremium ? dpsTheme.colors.status.completed.border : dpsTheme.colors.status.notPlanned.border}`,
                color: isPremium ? dpsTheme.colors.status.completed.text : dpsTheme.colors.status.notPlanned.text,
                flexShrink: 0
              }}
            >
              {isPremium ? (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  PREMIUM
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="5" y="11" width="14" height="10" rx="2" ry="2" />
                    <path d="M12 11V7a4 4 0 0 1 8 0" />
                  </svg>
                  GRATUIT
                </>
              )}
            </span>

            <div style={{
              fontSize: isMobile ? dpsTheme.typography.fontSize.base : dpsTheme.typography.fontSize.lg,
              fontWeight: dpsTheme.typography.fontWeight.semibold,
              color: dpsTheme.colors.text.primary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {diagnostic.diagnostic_data?.city}
            </div>
          </div>

          <div style={{
            fontSize: dpsTheme.typography.fontSize.sm,
            color: dpsTheme.colors.text.muted,
            marginBottom: '8px'
          }}>
            Créé le {new Date(diagnostic.created_at).toLocaleDateString('fr-FR', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
          </div>

          {isPremium && diagnostic.stripe_payment_intent_id && (
            <div style={{
              fontSize: dpsTheme.typography.fontSize.xs,
              color: dpsTheme.colors.status.completed.text,
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Accès premium débloqué
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', gap: '16px' }}>
          <div style={{
            background: scoreConfig.bg,
            border: `1px solid ${scoreConfig.border}`,
            borderRadius: dpsTheme.borderRadius.md,
            padding: isMobile ? '12px 16px' : '16px 20px',
            textAlign: 'center',
            minWidth: isMobile ? 'auto' : '100px'
          }}>
            <div style={{
              fontSize: dpsTheme.typography.fontSize.sm,
              color: dpsTheme.colors.text.muted,
              marginBottom: '4px'
            }}>
              Score
            </div>
            <div style={{
              fontSize: dpsTheme.typography.fontSize['3xl'],
              fontWeight: dpsTheme.typography.fontWeight.bold,
              color: scoreConfig.text
            }}>
              {currentScore}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: isMobile ? '100%' : 'auto' }}>
            {isPremium || hidePaymentButtons ? (
              <button
                onClick={onViewPremiumReport}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontWeight: 600,
                  fontSize: isMobile ? '13px' : '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  border: 'none',
                  background: dpsTheme.colors.accent.blue.gradient,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = dpsTheme.shadows.button;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M12 1v6m0 6v6m-8-8h6m6 0h6" />
                </svg>
                {isPremium ? 'Voir le rapport premium' : 'Accéder au rapport complet'}
              </button>
            ) : (
              <>
                <button
                  onClick={onViewFreeReport}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    fontWeight: 500,
                    fontSize: isMobile ? '13px' : '14px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    border: `1px solid ${dpsTheme.colors.border.primary}`,
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: dpsTheme.colors.text.secondary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  Rapport gratuit
                </button>

                <button
                  onClick={onViewPremiumReport}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    fontWeight: 600,
                    fontSize: isMobile ? '13px' : '14px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    border: 'none',
                    background: dpsTheme.colors.accent.blue.gradient,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = dpsTheme.shadows.button;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="5" y="11" width="14" height="10" rx="2" ry="2" />
                    <circle cx="12" cy="16" r="1" />
                    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                  </svg>
                  Débloquer premium
                </button>

                <button
                  onClick={onVerifyPayment}
                  disabled={isVerifying}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    fontWeight: 500,
                    fontSize: isMobile ? '11px' : '12px',
                    cursor: isVerifying ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    border: `1px solid ${dpsTheme.colors.status.completed.border}`,
                    background: 'rgba(16, 185, 129, 0.1)',
                    color: dpsTheme.colors.status.completed.text,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    opacity: isVerifying ? 0.6 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!isVerifying) {
                      e.currentTarget.style.background = 'rgba(16, 185, 129, 0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isVerifying) {
                      e.currentTarget.style.background = 'rgba(16, 185, 129, 0.1)';
                    }
                  }}
                >
                  {isVerifying ? (
                    <>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        style={{ animation: 'spin 1s linear infinite' }}
                      >
                        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                      </svg>
                      Vérification...
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
                      </svg>
                      Vérifier le paiement
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};